import React, { useState, useContext, useEffect } from "react";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import { makeStyles } from "@mui/styles";
import { UserContext } from "../../context/UserContext";
import { Alert, CircularProgress, Divider } from "@mui/material";
import axios from "axios";
import { ADMIN_URL } from "../../utils/constants";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import { useAlert } from "react-alert";

const useStyles = makeStyles({
	iphone: {
		width: 260,
		padding: 20,
		paddingTop: 0,
	},
	cardInfo: {
		display: "flex",
		width: "100%",
		justifyContent: "center",
	},
	cardForm: {
		display: "flex",
	},
	cardGrid: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
});

export default function Profile({ drawerWidth }) {
	const classes = useStyles();
	const { user, setUser } = useContext(UserContext);
	const [profileData, setProfileData] = useState({
		name: user.name,
		surname: user.surname,
		email: user.email,
	});
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();
	const alert = useAlert();

	const saveProfileData = async () => {
		setLoading(true);
		try {
			await axios.put(
				ADMIN_URL + "adminUsers/" + user.id,
				{
					name: profileData.name,
					surname: profileData.surname,
				},
				{
					headers: { authorization: localStorage.getItem("token") },
				}
			);
			setUser({
				...user,
				...{
					name: profileData.name,
					surname: profileData.surname,
				},
			});
			alert.show(t("general.success"), { type: "success" });
		} catch (error) {
			console.log(error);
		}
		setLoading(false);
	};

	return (
		<Box
			component="main"
			sx={{
				flexGrow: 1,
				bgcolor: "background.default",
				p: 3,
				width: { xs: "100%", sm: "calc(100vw - " + drawerWidth + "px)" },
			}}
		>
			<Toolbar />

			<Card variant="outlined">
				<>
					<CardContent>
						<Typography
							sx={{
								fontSize: 14,
								borderBottom: "1px solid #f0f0f0",
								minHeight: "35px",
								marginBottom: "20px",
								marginLeft: "-20px",
								marginRight: "-20px",
								padding: "0px 20px 20px",
							}}
							color="text.secondary"
							gutterBottom
						>
							{t("profile.title")}
						</Typography>
						<div className={classes.cardPageInfo}></div>
						<Typography variant="h6" sx={{ marginBottom: 2 }}>
							{t("profile.profile")}
						</Typography>
						<Grid
							xs={12}
							container
							spacing={2}
							justifyContent="space-between"
							sx={{ maxWidth: "calc(100% + 16px)!important" }}
						>
							<Grid xs={6} item>
								<FormControl
									fullWidth
									variant="standard"
									sx={{ marginTop: 2, marginBottom: 2 }}
									className={classes.formControl}
								>
									<InputLabel
										shrink
										htmlFor="name-input"
										sx={{
											marginTop: -2,
											fontWeight: 700,
											textTransform: "uppercase",
											fontSize: 12,
										}}
									>
										{t("profile.name")}
									</InputLabel>
									<TextField
										id="name-input"
										fullWidth
										variant="outlined"
										value={profileData && profileData.name}
										onChange={(e) => {
											setProfileData({
												...profileData,
												name: e.target.value,
											});
										}}
									/>
								</FormControl>
							</Grid>
							<Grid xs={6} item>
								<FormControl
									fullWidth
									variant="standard"
									sx={{ marginTop: 2, marginBottom: 2 }}
									className={classes.formControl}
								>
									<InputLabel
										shrink
										htmlFor="surname-input"
										sx={{
											marginTop: -2,
											fontWeight: 700,
											textTransform: "uppercase",
											fontSize: 12,
										}}
									>
										{t("profile.surname")}
									</InputLabel>
									<TextField
										id="surname-input"
										fullWidth
										variant="outlined"
										value={profileData && profileData.surname}
										onChange={(e) => {
											setProfileData({
												...profileData,
												surname: e.target.value,
											});
										}}
									/>
								</FormControl>
							</Grid>
						</Grid>
						<FormControl
							fullWidth
							variant="standard"
							sx={{ marginTop: 2, marginBottom: 2 }}
							className={classes.formControl}
						>
							<InputLabel
								shrink
								htmlFor="email-input"
								sx={{
									marginTop: -2,
									fontWeight: 700,
									textTransform: "uppercase",
									fontSize: 12,
								}}
							>
								{t("profile.email")}
							</InputLabel>
							<TextField
								id="address-input"
								fullWidth
								variant="outlined"
								value={profileData && profileData.email}
								onChange={(e) => {
									setProfileData({
										...profileData,
										email: e.target.email,
									});
								}}
								disabled
								InputProps={{ readOnly: true }}
							/>
						</FormControl>
					</CardContent>
					<CardActions sx={{ justifyContent: "right" }}>
						<Button
							variant="contained"
							onClick={() => {
								saveProfileData();
							}}
							size="small"
							disabled={loading}
						>
							{loading ? <CircularProgress size={12} /> : t("general.save")}
						</Button>
					</CardActions>
					<CardContent>
						<Divider sx={{ marginBottom: 2 }} />
						<Typography variant="h6" sx={{ marginBottom: 2 }}>
							{t("profile.billingInfo")}
						</Typography>
						<FormControl
							fullWidth
							variant="standard"
							sx={{ marginTop: 2, marginBottom: 2 }}
							className={classes.formControl}
						>
							<InputLabel
								shrink
								htmlFor="email-input"
								sx={{
									marginTop: -2,
									fontWeight: 700,
									textTransform: "uppercase",
									fontSize: 12,
								}}
							>
								{t("profile.address")}
							</InputLabel>
							<TextField
								id="address-input"
								fullWidth
								variant="outlined"
								value={
									user.customerDetails.address?.line1 +
									(user.customerDetails.address?.line2
										? ", " + user.customerDetails.address?.line2 ?? ""
										: "")
								}
								disabled
							/>
						</FormControl>

						<Grid
							xs={12}
							container
							spacing={2}
							sx={{ maxWidth: "calc(100% + 16px)!important" }}
						>
							<Grid xs={6} item>
								<FormControl
									fullWidth
									variant="standard"
									sx={{ marginTop: 2, marginBottom: 2 }}
									className={classes.formControl}
								>
									<InputLabel
										shrink
										htmlFor="postal-code-input"
										sx={{
											marginTop: -2,
											fontWeight: 700,
											textTransform: "uppercase",
											fontSize: 12,
										}}
									>
										{t("profile.postalCode")}
									</InputLabel>
									<TextField
										id="postal-code-input"
										fullWidth
										variant="outlined"
										value={user.customerDetails.address.postal_code}
										disabled
									/>
								</FormControl>
							</Grid>
							<Grid xs={6} item>
								<FormControl
									fullWidth
									variant="standard"
									sx={{ marginTop: 2, marginBottom: 2 }}
									className={classes.formControl}
								>
									<InputLabel
										shrink
										htmlFor="city-input"
										sx={{
											marginTop: -2,
											fontWeight: 700,
											textTransform: "uppercase",
											fontSize: 12,
										}}
									>
										{t("profile.city")}
									</InputLabel>
									<TextField
										id="city-input"
										fullWidth
										variant="outlined"
										value={user.customerDetails.address.city}
										disabled
									/>
								</FormControl>
							</Grid>
						</Grid>
						<Grid
							xs={12}
							container
							spacing={2}
							sx={{ maxWidth: "calc(100% + 16px)!important" }}
						>
							<Grid xs={6} item>
								<FormControl
									fullWidth
									variant="standard"
									sx={{ marginTop: 2, marginBottom: 2 }}
									className={classes.formControl}
								>
									<InputLabel
										shrink
										htmlFor="state-input"
										sx={{
											marginTop: -2,
											fontWeight: 700,
											textTransform: "uppercase",
											fontSize: 12,
										}}
									>
										{t("profile.state")}
									</InputLabel>
									<TextField
										id="state-input"
										fullWidth
										variant="outlined"
										value={user.customerDetails.address.state}
										disabled
									/>
								</FormControl>
							</Grid>
							<Grid xs={6} item>
								<FormControl
									fullWidth
									variant="standard"
									sx={{ marginTop: 2, marginBottom: 2 }}
									className={classes.formControl}
								>
									<InputLabel
										shrink
										htmlFor="country-input"
										sx={{
											marginTop: -2,
											fontWeight: 700,
											textTransform: "uppercase",
											fontSize: 12,
										}}
									>
										{t("profile.country")}
									</InputLabel>
									<TextField
										id="country-input"
										fullWidth
										variant="outlined"
										value={user.customerDetails.address.country}
										disabled
									/>
								</FormControl>
							</Grid>
						</Grid>
						<FormControl
							fullWidth
							variant="standard"
							sx={{ marginTop: 2, marginBottom: 2 }}
							className={classes.formControl}
						>
							<InputLabel
								shrink
								htmlFor="email-input"
								sx={{
									marginTop: -2,
									fontWeight: 700,
									textTransform: "uppercase",
									fontSize: 12,
								}}
							>
								{t("profile.cif")}
							</InputLabel>
							<TextField
								id="address-input"
								fullWidth
								variant="outlined"
								value={
									user.customerDetails?.tax_ids?.data &&
									user.customerDetails?.tax_ids?.data.length > 0
										? user.customerDetails?.tax_ids?.data[0].value
										: null
								}
								disabled
							/>
						</FormControl>
					</CardContent>
					<CardActions sx={{ justifyContent: "right" }}>
						<Button
							variant="contained"
							onClick={() => {
								window
									.open(
										"https://billing.stripe.com/p/login/bIYeYu2ea7Dd3Ic8ww?customer_email=" +
											user.email,
										"_blank"
									)
									.focus();
							}}
							size="small"
						>
							{t("profile.manage")}
						</Button>
					</CardActions>
				</>
			</Card>
		</Box>
	);
}
